import React from 'react';
import { humanize, titleize, unescapeHTML } from 'underscore.string';
import { has, sortBy } from 'lodash';
import formatMoney from 'utils/formatters/Money';
import { phoneFormatter } from 'components/modules/customers/formatters';
import { firstLetterTitlization, sluggify } from 'utils/localization/utils';
import { OPS_GROUP_OPTIONS } from 'constants/shipper';
import i18next from 'i18next';
import { formatDate } from 'utils/formatters/Date';
import { getUniqueStatusList } from 'utils/ContainerTrackAPIUtils';
import {
  getContainerStatuses,
  getUniqueMarketCodes,
  getColumnBlacklist,
} from 'utils/UnifiedViewWebAPIUtils';
import DescriptionListItem from 'components/v2/DescriptionListItem';
import CargomaticTooltip from 'components/v2/Tooltip';
import { billingStatusList } from 'constants/billing-status';
import { format, toDate, yearsAgo } from 'helpers/dates';
import { isUS } from 'helpers/helper_functions';
import { getReferenceNumbersMasterList } from 'utils/UserServiceAPIUtils';
import usStates from 'constants/us-states';
import {
  OPENTRACK_TRACKING_STATUS,
  MASTER_BOL_STATUS,
} from 'constants/Opentrack';
import {
  DEFAULT_DRAYAGE_COLS_OPS,
  STANDARD_REFERENCE_NUMBERS,
  STOP_TYPES,
  TIME_SELECTIONS,
} from './constants';
import {
  onDateCellEdit,
  onAdminNotesEdit,
  onShipmentCommentAdd,
  onShipmentChangeTags,
  onUpdatePODStatus,
} from './updateFunctions.js';
import CustomerMultiSearchFilter from './customFilters/CustomerMultiSearchFilter';
import TextFilterStartsWith from './customFilters/TextFilterStartsWith';
import TextFilterStartsWithNotStarts from './customFilters/TextFilterStartsWithNotStarts';

import { getMinValidDate } from './transformers';

import {
  tagFormatter,
  statusFormatter,
  portIntegrationsFormatter,
  marginFormatter,
  deliveryOrderFormatter,
  checkedFormatter,
  lineSeparatedStringFormatter,
  orderIDRenderer,
  opentrackTrackingStatusFormatter,
  shipmentIDRenderer,
  shipperShipmentIDRenderer,
  masterBolStatusFormatter,
} from './formatters';
import { shipmentStatuses } from '../shipments/mappings';
import CommentsEditor from './customCellEditors/CommentsEditor';
import TagsEditor from './customCellEditors/TagsEditor';
import { HoverBadges } from './customCellRenderers/HoverBadges';
import { showToast } from 'stores/ToastStore';
import { getDocumentTypes } from 'utils/DocumentsAPIUtils';
import { getAccessorials } from 'utils/AccessorialWebAPIUtils';
import { overrideBillingStatusReasonCodes } from 'constants/override-billing-status';
import { Badge } from '@cargomatic/ui';
import { getBillingStatusBadgeColor } from 'helpers/status-bar';
import { BillingStatusPendingReviewEditor } from './customCellEditors/BillingStatusPendingReviewEditor';
import EditButton from 'components/v2/buttons/EditButton';
import SessionStore from 'stores/SessionStore';

const ITEMS_PER_PAGE_LIMIT = 10000;
async function fetchReferenceNumbersMasterList() {
  try {
    const skip = 0;
    const response = await getReferenceNumbersMasterList(
      ITEMS_PER_PAGE_LIMIT,
      skip,
    );
    const queryResult = response?.data?.getReferenceNumbersQuery;
    if (!queryResult) {
      throw new Error('No response from API');
    }
    const { reference_numbers } = queryResult;
    return (reference_numbers ?? []).map((refNum) => refNum.slug);
  } catch (_) {
    showToast(i18next.t('ERROR_FETCHING_DATA'), null, 'error');
  }
}

async function getAvailableAccessorials() {
  const data = await getAccessorials({ paginate: false });
  return (data?.entity?.items ?? []).map((item) => item.slug);
}

async function getAvailableDocumentTypes(listType) {
  try {
    const response = await getDocumentTypes(listType);
    const data = response?.data?.getAvailableDocumentTypes;
    if (!data) {
      throw new Error('No response from API');
    }
    return (data ?? []).map((item) => item.localizationSlug);
  } catch (e) {
    console.error(e);
    showToast(i18next.t('ERROR_FETCHING_DATA'), null, 'error');
  }
}

const POD_STATUS_LIST = ['verified', 'missing', 'invalid'];
const POD_STATUS_MAP = {
  verified: 'POD_STATUS_VERIFIED',
  unverified: 'POD_STATUS_UNVERIFIED',
  missing: 'POD_STATUS_MISSING',
  invalid: 'POD_STATUS_INVALID',
};

const YEARS_BACK = 1;

const translateFormatter = (cell) => {
  const { value } = cell;

  if (value || typeof value === 'boolean') {
    return i18next.t(sluggify(value.toString())) || null;
  }
  return cell.value || null;
};

const convertDateTimeWithTimezone = (
  date,
  dateFormat = null,
  timeFormat = null,
  timezone = null,
  showTimezone = false,
) => {
  return formatDate(date, {
    dateFormat,
    timeFormat,
    timezone,
    showTimezone,
    t: i18next.t,
  });
};

const dateFormatter = (cell) => {
  if (cell.data) {
    const {
      data: { order_timezone },
      value,
    } = cell;
    if (value) {
      const timezone = order_timezone || DEFAULT_TIMEZONE;
      return convertDateTimeWithTimezone(
        value,
        DEFAULT_DATE_FORMAT,
        null,
        timezone,
        false,
      );
    }
  }
  return '';
};

const dateWithTimeFormatter = (cell) => {
  if (cell.data) {
    const {
      data: { order_timezone },
      value,
    } = cell;
    if (value) {
      const timezone = order_timezone || DEFAULT_TIMEZONE;
      return convertDateTimeWithTimezone(
        value,
        DEFAULT_DATE_FORMAT,
        DEFAULT_TIME_FORMAT,
        timezone,
        true,
      );
    }
  }
  return '';
};

const nameRenderer = (param) => {
  if (param.value) return unescapeHTML(param.value);
  return null;
};

// minimal valid date for date filters
const minValidDate = format(
  toDate(yearsAgo(new Date(), YEARS_BACK)),
  'yyyy-MM-dd',
);

const dateServerSideFilterParams = {
  maxNumConditions: 10,
  inRangeInclusive: true,
  buttons: ['reset', 'apply'],
  minValidDate,
  filterOptions: [
    {
      displayKey: 'equals',
      displayName: 'Equals',
      predicate() {
        return true;
      },
    },
    {
      displayKey: 'notEqual',
      displayName: 'Not equal',
      predicate() {
        return true;
      },
    },
    {
      displayKey: 'before',
      displayName: 'Before',
      predicate() {
        return true;
      },
    },
    {
      displayKey: 'after',
      displayName: 'After',
      predicate() {
        return true;
      },
    },
    {
      displayKey: 'inRange',
      displayName: 'Between',
      inRangeInclusive: true,
      numberOfInputs: 2,
      predicate() {
        return true;
      },
    },
    {
      displayKey: 'blank',
      displayName: 'Blank',
      numberOfInputs: 0,
      predicate() {
        return true;
      },
    },
    {
      displayKey: 'notBlank',
      displayName: 'Not blank',
      numberOfInputs: 0,
      predicate() {
        return true;
      },
    },
    ...TIME_SELECTIONS.map((item) => ({
      displayKey: item.localizationSlug,
      displayName: item.name,
      numberOfInputs: 0,
      predicate() {
        return true;
      },
    })),
  ],
};

const textDefaultFilterParams = {
  maxNumConditions: 10,
  buttons: ['reset', 'apply'],
  filterOptions: [
    'startsWith',
    {
      displayKey: 'notStartsWith',
      displayName: 'Does not start with',
      predicate: ([filterValue], cellValue) => {
        return !cellValue.startsWith(filterValue);
      },
    },
    'endsWith',
    'equals',
    'notEqual',
    'blank',
    'notBlank',
  ],
};

const booleanDefaultFilterParams = {
  buttons: ['reset', 'apply'],
  suppressMiniFilter: true,
  suppressSelectAll: true,
  values: [true, false],
};

const numberDefaultFilterParams = {
  buttons: ['reset', 'apply'],
  maxNumConditions: 10,
};

const LOADED_PICKUP_COL_GROUP_BASE = {
  groupId: 'loaded_pickup_stops',
  headerName: 'Loaded Pickup',
  hide: false,
};

const LOADED_DELIVERY_COL_GROUP_BASE = {
  groupId: 'loaded_delivery_stops',
  headerName: 'Loaded Delivery',
  hide: false,
};

const EMPTY_DELIVERY_COL_GROUP_BASE = {
  groupId: 'empty_delivery_stops',
  headerName: 'Empty Delivery',
  hide: false,
};

const EMPTY_PICKUP_COL_GROUP_BASE = {
  groupId: 'empty_pickup_stops',
  headerName: 'Empty Pickup',
  hide: false,
};

const OFFSITE_LOADED_PICKUP_COL_GROUP_BASE = {
  groupId: 'offsite_loaded_pickup_stops',
  headerName: 'Offsite Loaded Pickup',
  hide: false,
};

const OFFSITE_LOADED_DELIVERY_COL_GROUP_BASE = {
  groupId: 'offsite_loaded_delivery_stops',
  headerName: 'Offsite Loaded Delivery',
  hide: false,
};

const EMPTY_OFFSITE_DELIVERY_COL_GROUP_BASE = {
  groupId: 'empty_offsite_delivery_stops',
  headerName: 'Offsite Empty Delivery',
  hide: false,
};

const EMPTY_OFFSITE_PICKUP_COL_GROUP_BASE = {
  groupId: 'empty_offsite_pickup_stops',
  headerName: 'Offsite Empty Pickup',
  hide: false,
};

const STOP_TYPES_COL_GROUP_BASE_MAP = {
  loaded_pickup: LOADED_PICKUP_COL_GROUP_BASE,
  loaded_delivery: LOADED_DELIVERY_COL_GROUP_BASE,
  empty_delivery: EMPTY_DELIVERY_COL_GROUP_BASE,
  empty_pickup: EMPTY_PICKUP_COL_GROUP_BASE,
  offsite_loaded_pickup: OFFSITE_LOADED_PICKUP_COL_GROUP_BASE,
  offsite_loaded_delivery: OFFSITE_LOADED_DELIVERY_COL_GROUP_BASE,
  empty_offsite_delivery: EMPTY_OFFSITE_DELIVERY_COL_GROUP_BASE,
  empty_offsite_pickup: EMPTY_OFFSITE_PICKUP_COL_GROUP_BASE,
};

const STOP_SEQUENCE_COL_BASE = {
  filter: false,
  width: 100,
  cellDataType: 'number',
  type: 'rightAligned',
};

const STOP_ADDRESS_COL_BASE = {
  width: 400,
};

const STOP_DATE_WITH_TIMESTAMP_COL_BASE = {
  valueFormatter: dateWithTimeFormatter,
  filter: 'agDateColumnFilter',
  floatingFilter: true,
  width: 200,
  filterParams: dateServerSideFilterParams,
  cellDataType: 'dateString',
};

const STOP_WINDOW_START_COL_BASE = {
  valueFormatter: dateWithTimeFormatter,
  filter: 'agDateColumnFilter',
  floatingFilter: true,
  width: 200,
  filterParams: dateServerSideFilterParams,
  cellDataType: 'dateString',
};

const STOP_WINDOW_END_COL_BASE = {
  valueFormatter: dateWithTimeFormatter,
  filter: 'agDateColumnFilter',
  floatingFilter: true,
  width: 200,
  filterParams: dateServerSideFilterParams,
  cellDataType: 'dateString',
};

const STANDARD_REFERENCE_NUMBER_COL_BASE = {
  autoHeight: true,
  filter: TextFilterStartsWith,
  columnGroupShow: 'open',
  cellStyle: { whiteSpace: 'pre' },
};

const stopGroupFieldRegExp =
  /(?<prefix>[a-zA-Z_]+)__(?<stopGroupIndex>\d+)__(?<field>[a-zA-Z_]+)/g;

export function getStopGroupFieldColId({ prefix, stopGroupIndex, field }) {
  return `${prefix}__${stopGroupIndex}__${field}`;
}

export function getStopPrefixAndField(colId = '') {
  const matches = [...colId.matchAll(stopGroupFieldRegExp)];
  if (!matches[0]) {
    return {
      prefix: null,
      stopGroupIndex: null,
      field: null,
      isStopGroupField: false,
    };
  }

  const groups = matches[0]?.groups || {};
  const prefix = groups?.prefix;
  const stopGroupIndex = groups?.stopGroupIndex;
  const field = groups?.field;

  const parsedIndex = parseInt(stopGroupIndex, 10);
  const res = {
    prefix,
    stopGroupIndex: parsedIndex,
    field,
    isStopGroupField: !!prefix && !Number.isNaN(parsedIndex) && !!field,
  };

  return res;
}

const STOP_CONTAINER_COL_BASE = {
  filter: TextFilterStartsWith,
};

const STOP_EQUIPMENT_TYPE_COL_BASE = {
  headerName: 'Equipment Type',
  cellRenderer: translateFormatter,
};

const STOP_EQUIPMENT_SIZE_COL_BASE = {
  headerName: 'Equipment Size',
  type: 'rightAligned',
};

const STOP_CHASSIS_NUMBER_BASE = {
  filter: TextFilterStartsWith,
};
const STOP_STATUS_COL_BASE = {
  cellRenderer: statusFormatter,
  filter: 'agSetColumnFilter',
  filterParams: {
    buttons: ['reset', 'apply'],
    values: [
      'Unstarted',
      'Arriving',
      'Arrived',
      'Complete',
      'Canceled',
      'Attempt',
    ],
    // turn off standard sorting of values
    comparator: () => 1,
  },
};

function getStopFields(stopType, index) {
  const prefix = `${stopType}__${index}__`;

  const columnGroupShow = 'open';

  return [
    {
      headerName: 'Sequence',
      field: `${prefix}stop_sequence`,
      ...STOP_SEQUENCE_COL_BASE,
      columnGroupShow,
    },
    {
      headerName: 'Address',
      field: `${prefix}stop_address`,
      ...STOP_ADDRESS_COL_BASE,
      filterParams: {
        ...textDefaultFilterParams,
      },
    },
    {
      headerName: 'Window Start',
      field: `${prefix}stop_window_start`,
      ...STOP_WINDOW_START_COL_BASE,
      columnGroupShow,
    },
    {
      headerName: 'Window End',
      field: `${prefix}stop_window_end`,
      ...STOP_WINDOW_END_COL_BASE,
      columnGroupShow,
    },
    {
      headerName: 'Arrival Actual',
      field: `${prefix}stop_arrived_date`,
      ...STOP_DATE_WITH_TIMESTAMP_COL_BASE,
      columnGroupShow,
    },
    {
      headerName: 'Completed Actual',
      field: `${prefix}stop_completed_date`,
      ...STOP_DATE_WITH_TIMESTAMP_COL_BASE,
      columnGroupShow,
    },
    {
      headerName: 'Container',
      field: `${prefix}stop_equipment_id`,
      ...STOP_CONTAINER_COL_BASE,
      columnGroupShow,
    },
    {
      headerName: 'Equipment Type',
      field: `${prefix}stop_equipment_type`,
      ...STOP_EQUIPMENT_TYPE_COL_BASE,
      columnGroupShow,
      filterParams: {
        ...textDefaultFilterParams,
      },
    },
    {
      headerName: 'Equipment Size',
      field: `${prefix}stop_equipment_size`,
      ...STOP_EQUIPMENT_SIZE_COL_BASE,
      columnGroupShow,
      filterParams: {
        ...textDefaultFilterParams,
      },
    },
    {
      headerName: 'Chassis Number',
      field: `${prefix}stop_chassis_number`,
      ...STOP_CHASSIS_NUMBER_BASE,
      columnGroupShow,
    },
    {
      headerName: 'Status',
      field: `${prefix}stop_status`,
      ...STOP_STATUS_COL_BASE,
      columnGroupShow,
    },
  ];
}

function getStopGroups(stopType, stopsCount) {
  const children = Array.from({ length: stopsCount }).reduce(
    (stopGroups, _, index) => {
      const children = getStopFields(stopType, index);
      const headerName = STOP_TYPES_COL_GROUP_BASE_MAP[stopType].headerName;

      const group = {
        groupId: `${stopType}__${index}`,
        hide: false,
        openByDefault: index === 0,
        headerName: `${headerName} ${index + 1}`,
        marryChildren: false,
        children,
      };

      return stopGroups.concat(group);
    },
    [],
  );
  const group = {
    ...STOP_TYPES_COL_GROUP_BASE_MAP[stopType],
    openByDefault: true,
    marryChildren: false,
    children,
  };
  return group;
}

const SHORTHAUL_STOP_TYPES = ['loaded_pickup', 'loaded_delivery'];

const DRAYAGE_STOP_TYPES = [
  ...SHORTHAUL_STOP_TYPES,
  'empty_pickup',
  'empty_delivery',
  'offsite_loaded_delivery',
  'offsite_loaded_pickup',
  'empty_offsite_pickup',
  'empty_offsite_delivery',
];

const DRAYAGE_STOPS_LIMIT = 3;
export function getDrayageStopGroupsColumnDefs() {
  return DRAYAGE_STOP_TYPES.reduce(
    (stopsColGroups, stopType) =>
      stopsColGroups.concat(getStopGroups(stopType, DRAYAGE_STOPS_LIMIT)),
    [],
  );
}

const SHORTHAUL_STOPS_LIMIT = 1;
export function getShorthaulStopGroupsColumnDefs() {
  return SHORTHAUL_STOP_TYPES.reduce(
    (stopsColGroups, stopType) =>
      stopsColGroups.concat(getStopGroups(stopType, SHORTHAUL_STOPS_LIMIT)),
    [],
  );
}

function getStandardReferenceNumbersFields() {
  const valueGetter = (params, field) => {
    if (params && params.data && params.data.reference_numbers) {
      const {
        data: { reference_numbers: ref },
      } = params;

      const data = ref.filter((r) => {
        return (
          !has(r, 'stop_id') &&
          r.name === field &&
          STANDARD_REFERENCE_NUMBERS.includes(field)
        );
      });
      return data
        .map((r) => {
          return r.value || '';
        })
        .join('\n');
    }
    return null;
  };
  const cellRenderer = (param, field) => {
    if (param.data && param.data.reference_numbers) {
      const whitelist = param.data.reference_numbers.filter((f) => {
        return (
          [field].indexOf(f.name) > -1 &&
          STANDARD_REFERENCE_NUMBERS.includes(field)
        );
      });
      return whitelist
        .map((r) => {
          return r.value;
        })
        .join('\n');
    }
    return null;
  };

  return [
    {
      headerName: 'Delivery Order',
      field: 'delivery_order',
      valueGetter: (params) => valueGetter(params, 'delivery_order'),
      cellRenderer: (params) => cellRenderer(params, 'delivery_order'),
      ...STANDARD_REFERENCE_NUMBER_COL_BASE,
    },
    {
      headerName: 'Gate Dropoff',
      field: 'gate_dropoff',
      valueGetter: (params) => valueGetter(params, 'gate_dropoff'),
      cellRenderer: (params) => cellRenderer(params, 'gate_dropoff'),
      ...STANDARD_REFERENCE_NUMBER_COL_BASE,
    },
    {
      headerName: 'Gate Pickup',
      field: 'gate_pickup',
      valueGetter: (params) => valueGetter(params, 'gate_pickup'),
      cellRenderer: (params) => cellRenderer(params, 'gate_pickup'),
      ...STANDARD_REFERENCE_NUMBER_COL_BASE,
    },
    {
      headerName: 'House Bill Number',
      field: 'house_bill_number',
      valueGetter: (params) => valueGetter(params, 'house_bill_number'),
      cellRenderer: (params) => cellRenderer(params, 'house_bill_number'),
      ...STANDARD_REFERENCE_NUMBER_COL_BASE,
    },
    {
      headerName: 'ISA',
      field: 'isa',
      valueGetter: (params) => valueGetter(params, 'isa'),
      cellRenderer: (params) => cellRenderer(params, 'isa'),
      ...STANDARD_REFERENCE_NUMBER_COL_BASE,
    },
    {
      headerName: 'Master BOL',
      field: 'master_bol',
      valueGetter: (params) => valueGetter(params, 'master_bol'),
      cellRenderer: (params) => cellRenderer(params, 'master_bol'),
      ...STANDARD_REFERENCE_NUMBER_COL_BASE,
    },
    {
      headerName: 'MG Load ID',
      field: 'mg_load_id',
      valueGetter: (params) => valueGetter(params, 'mg_load_id'),
      cellRenderer: (params) => cellRenderer(params, 'mg_load_id'),
      ...STANDARD_REFERENCE_NUMBER_COL_BASE,
    },
    {
      headerName: 'MO Number',
      field: 'mo_number',
      valueGetter: (params) => valueGetter(params, 'mo_number'),
      cellRenderer: (params) => cellRenderer(params, 'mo_number'),
      ...STANDARD_REFERENCE_NUMBER_COL_BASE,
    },
    {
      headerName: 'PU Number',
      field: 'pu_number',
      valueGetter: (params) => valueGetter(params, 'pu_number'),
      cellRenderer: (params) => cellRenderer(params, 'pu_number'),
      ...STANDARD_REFERENCE_NUMBER_COL_BASE,
    },
    {
      headerName: 'Seal Number',
      field: 'seal_number',
      valueGetter: (params) => valueGetter(params, 'seal_number'),
      cellRenderer: (params) => cellRenderer(params, 'seal_number'),
      ...STANDARD_REFERENCE_NUMBER_COL_BASE,
    },
    {
      headerName: 'Tracking ID',
      field: 'tracking_id',
      valueGetter: (params) => valueGetter(params, 'tracking_id'),
      cellRenderer: (params) => cellRenderer(params, 'tracking_id'),
      ...STANDARD_REFERENCE_NUMBER_COL_BASE,
    },
    {
      headerName: 'TO',
      field: 'truck_order',
      valueGetter: (params) => valueGetter(params, 'truck_order'),
      cellRenderer: (params) => cellRenderer(params, 'truck_order'),
      ...STANDARD_REFERENCE_NUMBER_COL_BASE,
    },
    {
      headerName: 'VRID',
      field: 'VRID',
      valueGetter: (params) => valueGetter(params, 'VRID'),
      cellRenderer: (params) => cellRenderer(params, 'VRID'),
      ...STANDARD_REFERENCE_NUMBER_COL_BASE,
    },
    {
      headerName: 'Work Order Number',
      field: 'work_order_number',
      valueGetter: (params) => valueGetter(params, 'work_order_number'),
      cellRenderer: (params) => cellRenderer(params, 'work_order_number'),
      ...STANDARD_REFERENCE_NUMBER_COL_BASE,
    },
    {
      headerName: 'Last Move',
      field: 'last_move',
      valueGetter: (params) => valueGetter(params, 'last_move'),
      cellRenderer: (params) => cellRenderer(params, 'last_move'),
      ...STANDARD_REFERENCE_NUMBER_COL_BASE,
    },
    {
      headerName: 'Final Destination',
      field: 'final_destination',
      valueGetter: (params) => valueGetter(params, 'final_destination'),
      cellRenderer: (params) => cellRenderer(params, 'final_destination'),
      ...STANDARD_REFERENCE_NUMBER_COL_BASE,
    },
    {
      headerName: 'Position',
      field: 'position',
      valueGetter: (params) => valueGetter(params, 'position'),
      cellRenderer: (params) => cellRenderer(params, 'position'),
      ...STANDARD_REFERENCE_NUMBER_COL_BASE,
    },
    {
      headerName: 'Cargo Weight (LB)',
      field: 'cargo_weight',
      valueGetter: (params) => valueGetter(params, 'cargo_weight'),
      cellRenderer: (params) => cellRenderer(params, 'cargo_weight'),
      ...STANDARD_REFERENCE_NUMBER_COL_BASE,
    },
    {
      headerName: 'T State',
      field: 't_state',
      valueGetter: (params) => valueGetter(params, 't_state'),
      cellRenderer: (params) => cellRenderer(params, 't_state'),
      ...STANDARD_REFERENCE_NUMBER_COL_BASE,
    },
    {
      headerName: 'Freight Kind',
      field: 'freight_kind',
      valueGetter: (params) => valueGetter(params, 'freight_kind'),
      cellRenderer: (params) => cellRenderer(params, 'freight_kind'),
      ...STANDARD_REFERENCE_NUMBER_COL_BASE,
    },
    {
      headerName: 'Over Weight',
      field: 'over_weight',
      valueGetter: (params) => valueGetter(params, 'over_weight'),
      cellRenderer: (params) => cellRenderer(params, 'over_weight'),
      ...STANDARD_REFERENCE_NUMBER_COL_BASE,
    },
    {
      headerName: 'Requires Power',
      field: 'reqs_power',
      valueGetter: (params) => valueGetter(params, 'reqs_power'),
      cellRenderer: (params) => cellRenderer(params, 'reqs_power'),
      ...STANDARD_REFERENCE_NUMBER_COL_BASE,
    },
    {
      headerName: 'Unit Impediments',
      field: 'unit_impediments',
      valueGetter: (params) => valueGetter(params, 'unit_impediments'),
      cellRenderer: (params) => cellRenderer(params, 'unit_impediments'),
      ...STANDARD_REFERENCE_NUMBER_COL_BASE,
    },
    {
      headerName: 'Booking Number',
      field: 'booking_number',
      valueGetter: (params) => valueGetter(params, 'booking_number'),
      cellRenderer: (params) => cellRenderer(params, 'booking_number'),
      ...STANDARD_REFERENCE_NUMBER_COL_BASE,
    },
    {
      headerName: 'Partner Shipment Id',
      field: 'partnerShipmentId',
      valueGetter: (params) => valueGetter(params, 'partnerShipmentId'),
      cellRenderer: (params) => cellRenderer(params, 'partnerShipmentId'),
      ...STANDARD_REFERENCE_NUMBER_COL_BASE,
    },
    {
      headerName: 'Billing',
      field: 'Billing',
      valueGetter: (params) => valueGetter(params, 'Billing'),
      cellRenderer: (params) => cellRenderer(params, 'Billing'),
      ...STANDARD_REFERENCE_NUMBER_COL_BASE,
    },
    {
      headerName: 'Mode',
      field: 'Mode',
      valueGetter: (params) => valueGetter(params, 'Mode'),
      cellRenderer: (params) => cellRenderer(params, 'Mode'),
      ...STANDARD_REFERENCE_NUMBER_COL_BASE,
    },
    {
      headerName: 'Shipment',
      field: 'Shipment',
      valueGetter: (params) => valueGetter(params, 'Shipment'),
      cellRenderer: (params) => cellRenderer(params, 'Shipment'),
      ...STANDARD_REFERENCE_NUMBER_COL_BASE,
    },
    {
      headerName: 'PO Number',
      field: 'po_number',
      valueGetter: (params) => valueGetter(params, 'po_number'),
      cellRenderer: (params) => cellRenderer(params, 'po_number'),
      ...STANDARD_REFERENCE_NUMBER_COL_BASE,
    },
    {
      headerName: 'CS Number',
      field: 'CS NUMBER',
      valueGetter: (params) => valueGetter(params, 'CS NUMBER'),
      cellRenderer: (params) => cellRenderer(params, 'CS NUMBER'),
      ...STANDARD_REFERENCE_NUMBER_COL_BASE,
    },
  ];
}

export function getBillingStatusStateGroup() {
  return [
    {
      priority: 0,
      headerName: 'Billing Status',
      groupId: 'billing_status',
      hide: false,
      marryChildren: false,
      children: [
        {
          headerName: 'Billing Status',
          field: 'billing_status_state_billing_status',
          cellRenderer: ({ value }) => {
            return (
              <Badge color={getBillingStatusBadgeColor(value)}>
                {titleize(humanize(i18next.t(value)))}
              </Badge>
            );
          },
          filter: 'agSetColumnFilter',
          filterParams: {
            buttons: ['reset', 'apply'],
            valueFormatter: ({ value }) => {
              return titleize(humanize(i18next.t(value)));
            },
            values: billingStatusList,
          },
          autoHeight: true,
        },
        {
          groupId: 'override_billing_status',
          hide: false,
          headerName: 'Override Billing Status',

          children: [
            {
              headerName: 'Note',
              field:
                'billing_status_state_pause_automated_billing_evaluation_reason_code',
            },
            {
              headerName: 'Reason Codes',
              minWidth: 250,
              field:
                'billing_status_state_pause_automated_billing_evaluation_reason_codes',
              cellRenderer: ({ value, node, column, api }) => {
                const handleClick = (e) => {
                  e.stopPropagation();
                  e.preventDefault();
                  api.startEditingCell({
                    rowIndex: node.rowIndex,
                    colKey: column.getId(),
                  });
                };
                const showEditButton =
                  SessionStore.isBillingStatusAdmin() ||
                  SessionStore.isBillingStatusManager() ||
                  SessionStore.isBillingStatusUser();
                return (
                  <div className="flex justify-between">
                    {value && (
                      <ul className="flex-1">
                        {(value ?? []).map((item) => (
                          <li key={item}>{i18next.t(item)}</li>
                        ))}
                      </ul>
                    )}
                    {(value?.length ?? 0) === 0 && <span>&nbsp;</span>}
                    {showEditButton && (
                      <EditButton
                        className="flex-shrink-0 flex-grow-0"
                        size="small"
                        onClick={handleClick}
                      />
                    )}
                  </div>
                );
              },
              cellEditor: BillingStatusPendingReviewEditor,
              filter: 'agSetColumnFilter',
              filterParams: {
                buttons: ['reset', 'apply'],
                valueFormatter: (params) =>
                  params.value ? i18next.t(params.value) : params.value,

                values: [null, ...overrideBillingStatusReasonCodes],
              },
              autoHeight: true,
              editable: true,
            },
          ],
        },
      ],
    },
    {
      priority: 1,
      headerName: 'Missing for Ready to Bill',
      groupId: 'billing_status_state',
      hide: false,
      marryChildren: false,
      children: [
        {
          headerName: 'Has Billable Charge',
          field:
            'billing_status_state_has_pending_shipper_accessorials_or_unbilled_receivables_charge',
          cellRenderer: checkedFormatter,
          wrapText: true,
          autoHeight: true,
          enableRowGroup: false,
          cellDataType: 'boolean',
          filter: 'agSetColumnFilter',
          filterParams: booleanDefaultFilterParams,
        },
        {
          headerName: 'Unbilled Charges',
          field:
            'billing_status_state_pending_shipper_accessorials_and_unbilled_receivables_charge_total',
          cellRenderer: ({ data }) => {
            if (
              !data.billing_status_state_pending_shipper_accessorials_and_unbilled_receivables_charge_total
            ) {
              return '—';
            }
            return formatMoney(
              data.billing_status_state_pending_shipper_accessorials_and_unbilled_receivables_charge_total,
              data?.shipment_currency,
            );
          },
          autoHeight: true,
          enableRowGroup: false,
          cellDataType: 'number',
          type: 'numericColumn',
          filter: 'agNumberColumnFilter',
        },
        {
          headerName: 'Required Docs',
          field: 'billing_status_state_has_required_docs',
          cellRenderer: checkedFormatter,
          wrapText: true,
          autoHeight: true,
          enableRowGroup: false,
          cellDataType: 'boolean',
          filter: 'agSetColumnFilter',
          filterParams: booleanDefaultFilterParams,
        },
        {
          headerName: 'Missing Docs',
          field: 'billing_status_state_missing_docs',
          cellRenderer: HoverBadges,
          minWidth: 300,
          enableRowGroup: false,
          wrapText: true,
          autoHeight: true,
          filter: 'agSetColumnFilter',
          filterParams: {
            buttons: ['reset', 'apply'],
            valueFormatter: (params) =>
              params.value
                ? titleize(humanize(i18next.t(sluggify(params.value))))
                : params.value,

            values: async (params) => {
              const data = await getAvailableDocumentTypes('shipment');

              params.success(
                (data || []).map((docType) => docType.toLowerCase()),
              );
            },
          },
        },
        {
          headerName: 'Required Ref Nums',
          field: 'billing_status_state_has_required_reference_numbers',
          cellRenderer: checkedFormatter,
          wrapText: true,
          autoHeight: true,
          enableRowGroup: false,
          cellDataType: 'boolean',
          filter: 'agSetColumnFilter',
          filterParams: booleanDefaultFilterParams,
        },
        {
          headerName: 'Missing Ref Nums',
          field: 'billing_status_state_missing_reference_numbers',
          cellRenderer: HoverBadges,
          minWidth: 350,
          wrapText: true,
          autoHeight: true,
          enableRowGroup: false,
          filter: 'agSetColumnFilter',
          filterParams: {
            buttons: ['reset', 'apply'],
            valueFormatter: (params) =>
              params.value
                ? titleize(humanize(i18next.t(sluggify(params.value))))
                : params.value,

            values: async (params) => {
              const referenceNumbers = await fetchReferenceNumbersMasterList();
              params.success(referenceNumbers);
            },
          },
        },
        {
          headerName: 'Minimum Accessorials',
          field: 'billing_status_state_has_required_accessorials',
          cellRenderer: checkedFormatter,
          wrapText: true,
          autoHeight: true,
          enableRowGroup: false,
          cellDataType: 'boolean',
          filter: 'agSetColumnFilter',
          filterParams: booleanDefaultFilterParams,
        },
        {
          headerName: 'Missing or Pending Accessorials',
          field:
            'billing_status_state_missing_and_pending_required_accessorial_types',
          cellRenderer: HoverBadges,
          minWidth: 350,
          wrapText: true,
          autoHeight: true,
          enableRowGroup: false,
          filter: 'agSetColumnFilter',
          filterParams: {
            buttons: ['reset', 'apply'],
            valueFormatter: (params) =>
              params.value
                ? titleize(humanize(i18next.t(sluggify(params.value))))
                : params.value,

            values: async (params) => {
              const data = await getAvailableAccessorials();
              params.success(data);
            },
          },
        },
        {
          headerName: 'Billable Stops Completed',
          field: 'billing_status_state_has_required_billable_stops',
          cellRenderer: checkedFormatter,
          wrapText: true,
          autoHeight: true,
          enableRowGroup: false,
          cellDataType: 'boolean',
          filter: 'agSetColumnFilter',
          filterParams: booleanDefaultFilterParams,
        },
        {
          headerName: 'Incomplete Billable Stops',
          field: 'billing_status_state_pending_billable_stops',
          minWidth: 350,
          cellRenderer: HoverBadges,
          wrapText: true,
          autoHeight: true,
          enableRowGroup: false,
          filter: 'agSetColumnFilter',
          filterParams: {
            valueFormatter: ({ value }) => i18next.t(sluggify(value)),
            buttons: ['reset', 'apply'],
            values: STOP_TYPES,
          },
        },
      ],
    },
  ];
}

export function getReferenceNumberGroup() {
  return [
    {
      headerName: 'Standard Reference Numbers',
      groupId: 'standard_reference_numbers',
      hide: false,
      marryChildren: false,
      children: getStandardReferenceNumbersFields(),
    },
  ];
}

export function getDrayageShipmentTypes() {
  return {
    headerName: 'Shipment Type',
    field: 'shipment_type',
    priority: 8,
    width: 100,
    cellRenderer: statusFormatter,
    filter: true,
    filterParams: {
      buttons: ['reset', 'apply'],
      values: ['drayage', 'rail'],
      suppressSorting: true,
      defaultToNothingSelected: false,
    },
  };
}

const COLUMN_DEFS = [
  {
    headerName: 'POD Status',
    field: 'pod_status',
    valueFormatter: (params) =>
      params.value ? i18next.t(POD_STATUS_MAP[params.value]) : '-',
    editable: true,
    cellEditor: 'agSelectCellEditor',
    cellEditorParams: {
      values: POD_STATUS_LIST,
    },
    updateFunc: onUpdatePODStatus,
    filter: 'agSetColumnFilter',
    filterParams: {
      valueFormatter: (params) =>
        params.value ? i18next.t(POD_STATUS_MAP[params.value]) : '-',
      values: POD_STATUS_LIST,
    },
  },
  {
    headerName: 'Driver Phone Number',
    field: 'driver_phone_number',
    filter: false,
    valueFormatter: phoneFormatter,
    suppressFiltersToolPanel: false,
  },
  {
    headerName: 'Tags',
    field: 'tags',
    filter: TextFilterStartsWithNotStarts,
    cellRenderer: tagFormatter,
    editable: true,
    cellEditorPopup: true,
    cellEditor: TagsEditor,
    width: 200,
    titleize: true,
    autoHeight: true,
    cellStyle: { 'white-space': 'normal' },
    cellEditorParams: { onShipmentChangeTags },
    filterParams: {
      ...textDefaultFilterParams,
    },
  },
  {
    headerName: 'Mode',
    field: 'mode',
    titleize: true,
    cellRenderer: translateFormatter,
    filterParams: {
      ...textDefaultFilterParams,
    },
  },
  {
    headerName: 'Move Type',
    field: 'move_type',
    cellRenderer: translateFormatter,
    filterParams: {
      ...textDefaultFilterParams,
    },
  },
  {
    headerName: 'Notes',
    field: 'notes',
    autoHeight: true,
    filterParams: {
      ...textDefaultFilterParams,
    },
  },
  {
    headerName: 'Admin Notes',
    field: 'admin_notes',
    editable: true,
    updateFunc: onAdminNotesEdit,
    cellEditor: 'agLargeTextCellEditor',
    cellEditorPopup: true,
    autoHeight: true,
    filterParams: {
      ...textDefaultFilterParams,
    },
  },
  {
    headerName: 'Cargo Description',
    field: 'cargo_description',
    filterParams: {
      ...textDefaultFilterParams,
    },
  },
  {
    headerName: 'Hazmat Description',
    field: 'hazmat_description',
    filterParams: {
      ...textDefaultFilterParams,
    },
  },
  {
    headerName: 'Origin',
    field: 'shipment_origin',
    priority: 35,
    titleize: true,
    filterParams: {
      ...textDefaultFilterParams,
    },
  },
  {
    headerName: 'Origin Name',
    field: 'shipment_origin_name',
    cellRenderer: nameRenderer,
    priority: 35,
    hide: true,
    filterParams: {
      ...textDefaultFilterParams,
    },
  },
  {
    headerName: 'Origin City',
    field: 'shipment_origin_city',
    priority: 35,
    hide: true,
    filterParams: {
      ...textDefaultFilterParams,
    },
  },
  {
    headerName: 'Origin State',
    field: 'shipment_origin_state',
    priority: 35,
    hide: true,
    filterParams: {
      ...textDefaultFilterParams,
    },
  },
  {
    headerName: 'Destination',
    field: 'shipment_destination',
    priority: 40,
    titleize: true,
    filterParams: {
      ...textDefaultFilterParams,
    },
  },
  {
    headerName: 'Destination Name',
    field: 'shipment_destination_name',
    cellRenderer: nameRenderer,
    priority: 40,
    hide: true,
    filterParams: {
      ...textDefaultFilterParams,
    },
  },
  {
    headerName: 'Destination City',
    field: 'shipment_destination_city',
    priority: 40,
    hide: true,
    filterParams: {
      ...textDefaultFilterParams,
    },
  },
  {
    headerName: 'Destination State',
    field: 'shipment_destination_state',
    priority: 40,
    hide: true,
    filterParams: {
      ...textDefaultFilterParams,
    },
  },
  {
    headerName: 'Shipment Type',
    field: 'shipment_type',
    priority: 8,
    width: 100,
    cellRenderer: statusFormatter,
    filter: true,
    filterParams: {
      buttons: ['reset', 'apply'],
      values: ['shorthaul', 'longhaul', 'dayrate'],
      suppressSorting: true,
      defaultToNothingSelected: false,
    },
  },
  {
    headerName: 'Hazmat Class',
    field: 'hazmat_class',
    capitalize: true,
    cellRenderer: translateFormatter,
    filterParams: {
      ...textDefaultFilterParams,
    },
  },
  {
    headerName: 'Originating',
    field: 'originating',
    titleize: true,
    filterParams: {
      ...textDefaultFilterParams,
    },
  },
  {
    headerName: 'Service Type',
    field: 'service_type',
    cellRenderer: translateFormatter,
    filterParams: {
      ...textDefaultFilterParams,
    },
  },
  {
    headerName: 'Load',
    titleize: true,
    field: 'load_details',
    autoHeight: true,
    priority: 45,
    cellRenderer: (param) => {
      const loadTypes = ['pallets', 'boxes'];

      if (param.value) {
        const splitted = param.value
          .replace('\n', '<br/>')
          .toString()
          .split(' ');

        return splitted
          .map((str) => {
            if (loadTypes.includes(str.toLowerCase())) {
              return i18next.t(sluggify(str));
            }
            return str;
          })
          .join(' ');
      }
      return '';
    },
    filterParams: {
      ...textDefaultFilterParams,
    },
  },
  {
    headerName: 'Reference Numbers',
    field: 'reference_numbers',
    autoHeight: true,
    priority: 7,
    valueGetter: (params) => {
      if (params && params.data && params.data.reference_numbers) {
        const {
          data: { reference_numbers: ref },
        } = params;
        const data = ref.filter((r) => {
          return !has(r, 'stop_id');
        });
        // exclude reference numbers that have their own columns
        const blacklist = [
          'order_id',
          'shipment_id',
          'equipment_id',
          'vessel_or_rail',
        ];
        return data
          .filter(
            (f) =>
              !blacklist.includes(f.name) &&
              !STANDARD_REFERENCE_NUMBERS.includes(f.name),
          )
          .map((r) => {
            return `${titleize(humanize(r.name))} ${r.value || ''}`;
          })
          .join('\n');
      }
      return null;
    },
    cellRenderer: (param) => {
      if (param.data && param.data[param.column.colId]) {
        // exclude reference numbers that have their own columns
        const blacklist = [
          'order_id',
          'shipment_id',
          'equipment_id',
          'vessel_or_rail',
        ];
        // exclude also standard reference numbers that have separate column group
        const whitelist = param.data[param.column.colId].filter(
          (f) =>
            !blacklist.includes(f.name) &&
            !STANDARD_REFERENCE_NUMBERS.includes(f.name),
        );
        const items = whitelist.map((r, index) => {
          const label = titleize(humanize(i18next.t(sluggify(r.name))));
          return (
            <DescriptionListItem
              dataTestId={`DescriptionListItem-5642de74-${index}`}
              className="mb-2"
              label={label}
            >
              {r.value}
            </DescriptionListItem>
          );
        });
        return (
          <CargomaticTooltip
            dataTestId="CargomaticTooltip-b9ff2365"
            body={() => items.slice(0, 2)}
            position="top"
          >
            <div data-testid="div-698dd925" className="invert">
              {items}
            </div>
          </CargomaticTooltip>
        );
      }
      return null;
    },
    filter: TextFilterStartsWith,
  },
  {
    headerName: 'Hazmat Packaging',
    field: 'hazmat_packaging_group',
    capitalize: true,
    cellRenderer: translateFormatter,
    filterParams: {
      ...textDefaultFilterParams,
    },
  },
  {
    headerName: 'Steam Ship/Railroad',
    field: 'steamship_or_railroad',
    filterParams: {
      ...textDefaultFilterParams,
    },
  },
  {
    headerName: 'Review Date',
    field: 'review_date',
    cellDataType: 'dateString',
    filter: 'agDateColumnFilter',
    filterParams: dateServerSideFilterParams,
    valueFormatter: dateFormatter,
    schema_value: 'review_date',
    editable: true,
    isDate: true,
    updateFunc: onDateCellEdit,
    floatingFilter: true,
  },
  {
    headerName: 'Accounting Review Date',
    field: 'accounting_review_date',
    cellDataType: 'dateString',
    filter: 'agDateColumnFilter',
    filterParams: dateServerSideFilterParams,
    valueFormatter: dateFormatter,
    schema_value: 'accounting_review_date',
    editable: true,
    isDate: true,
    updateFunc: onDateCellEdit,
    floatingFilter: true,
  },
  {
    headerName: 'Arrived Date',
    field: 'arrival_date',
    cellDataType: 'dateString',
    filter: 'agDateColumnFilter',
    filterParams: dateServerSideFilterParams,
    valueFormatter: dateFormatter,
    schema_value: 'arrival_date',
    editable: false,
    isDate: true,
    updateFunc: onDateCellEdit,
    floatingFilter: true,
  },
  {
    headerName: 'Discharge Date',
    field: 'discharge_date',
    cellDataType: 'dateString',
    filter: 'agDateColumnFilter',
    valueFormatter: dateFormatter,
    schema_value: 'discharge_date',
    filterParams: dateServerSideFilterParams,
    editable: false,
    isDate: true,
    updateFunc: onDateCellEdit,
    floatingFilter: true,
  },
  {
    headerName: 'Available Date',
    field: 'available_date',
    cellDataType: 'dateString',
    filter: 'agDateColumnFilter',
    valueFormatter: dateFormatter,
    schema_value: 'available_date',
    filterParams: dateServerSideFilterParams,
    editable: false,
    isDate: true,
    floatingFilter: true,
  },
  {
    headerName: 'Outgated Date',
    field: 'outgated_date',
    cellDataType: 'dateString',
    filter: 'agDateColumnFilter',
    valueFormatter: dateFormatter,
    schema_value: 'outgated_date',
    filterParams: dateServerSideFilterParams,
    editable: false,
    isDate: true,
    floatingFilter: true,
  },
  {
    headerName: 'Empty Ready for Pickup Date',
    field: 'empty_ready_for_pickup',
    filter: 'agDateColumnFilter',
    cellDataType: 'dateString',
    valueFormatter: dateFormatter,
    schema_value: 'empty_ready_for_pickup',
    filterParams: dateServerSideFilterParams,
    editable: false,
    isDate: true,
    floatingFilter: true,
  },
  {
    headerName: 'Empty Returned Date',
    field: 'empty_returned_date',
    cellDataType: 'dateString',
    filter: 'agDateColumnFilter',
    valueFormatter: dateFormatter,
    schema_value: 'empty_returned_date',
    filterParams: dateServerSideFilterParams,
    editable: false,
    isDate: true,
    floatingFilter: true,
  },
  {
    headerName: 'Last Cut Date',
    field: 'last_cut_date',
    cellDataType: 'dateString',
    filter: 'agDateColumnFilter',
    valueFormatter: dateFormatter,
    schema_value: 'last_cut_date',
    filterParams: dateServerSideFilterParams,
    editable: false,
    isDate: true,
    floatingFilter: true,
  },
  {
    headerName: 'Earliest Return Date',
    field: 'earliest_return_date',
    cellDataType: 'dateString',
    filter: 'agDateColumnFilter',
    valueFormatter: dateFormatter,
    schema_value: 'earliest_return_date',
    filterParams: dateServerSideFilterParams,
    editable: false,
    isDate: true,
    floatingFilter: true,
  },
  {
    headerName: 'Earliest Date to Get Empty',
    field: 'earliest_empty_date',
    cellDataType: 'dateString',
    filter: 'agDateColumnFilter',
    valueFormatter: dateFormatter,
    schema_value: 'earliest_empty_date',
    filterParams: dateServerSideFilterParams,
    editable: false,
    isDate: true,
    floatingFilter: true,
  },
  {
    headerName: 'Weight',
    titleize: true,
    field: 'total_weight',
    filter: 'agNumberColumnFilter',
    cellDataType: 'number',
    type: 'rightAligned',
    filterParams: {
      ...numberDefaultFilterParams,
    },
  },
  {
    headerName: 'Required Docs',
    field: 'has_required_docs',
    cellRenderer: checkedFormatter,
    filter: 'agSetColumnFilter',
    filterParams: booleanDefaultFilterParams,
  },
  {
    headerName: 'Shipment ID',
    field: 'shipment_id',
    width: 150,
    pinnedFor: ['shorthaul', 'drayage'],
    filter: TextFilterStartsWith,
    headerCheckboxSelection: true,
    checkboxSelection: true,
    priority: 0,
    cellRenderer: shipmentIDRenderer,
  },
  {
    headerName: 'Shipment Status',
    field: 'shipmentStatus',
    pinnedFor: ['shorthaul', 'drayage'],
    cellRenderer: statusFormatter,
    filter: true,
    filterParams: {
      buttons: ['reset', 'apply'],
      values: shipmentStatuses.map((shipmentStatus) => shipmentStatus.message),
      suppressSorting: true,
    },
    floatingFilter: true,
    priority: 5,
  },
  {
    headerName: 'Order ID',
    field: 'order_id',
    width: 150,
    priority: 10,
    filter: TextFilterStartsWith,
    cellRenderer: orderIDRenderer,
  },
  {
    headerName: 'Order Status',
    field: 'orderStatus',
    width: 125,
    cellRenderer: statusFormatter,
    filter: TextFilterStartsWithNotStarts,
    priority: 15,
  },
  {
    headerName: 'Container Number',
    field: 'equipment_id',
    valueGetter: (params) => {
      if (params?.data?.reference_numbers) {
        return params.data.reference_numbers
          .filter((r) => r.name === 'equipment_id')
          .map((r) => r.optimized || r.value || '')
          .join('\n');
      }
      return '';
    },
    ...STANDARD_REFERENCE_NUMBER_COL_BASE,
    priority: 16,
  },
  {
    headerName: 'Chassis Number',
    field: 'chassis_number',
    valueGetter: (params) => {
      if (params?.data?.reference_numbers) {
        return params.data.reference_numbers
          .filter((r) => r.name === 'chassis_number')
          .map((r) => r.optimized || r.value || '')
          .join('\n');
      }
      return '';
    },
    ...STANDARD_REFERENCE_NUMBER_COL_BASE,
    priority: 17,
  },
  {
    headerName: 'Trailer Number',
    field: 'trailer_number',
    valueGetter: (params) => {
      if (params?.data?.reference_numbers) {
        return params.data.reference_numbers
          .filter((r) => r.name === 'trailer_number')
          .map((r) => r.optimized || r.value || '')
          .join('\n');
      }
      return '';
    },
    ...STANDARD_REFERENCE_NUMBER_COL_BASE,
    priority: 18,
  },
  {
    headerName: 'Order Type',
    field: 'order_type',
    width: 125,
    priority: 20,
    filterParams: {
      ...textDefaultFilterParams,
    },
    cellRenderer: translateFormatter,
  },
  {
    headerName: 'Margin',
    titleize: true,
    field: 'margin',
    cellRenderer: marginFormatter,
    filter: 'agNumberColumnFilter',
    cellDataType: 'number',
    type: 'rightAligned',
    priority: 100,
    filterParams: {
      ...numberDefaultFilterParams,
    },
  },
  {
    headerName: 'Market',
    titleize: true,
    field: 'marketCode',
    filter: 'agSetColumnFilter',
    filterParams: {
      buttons: ['reset', 'apply'],
      values: async (params) => {
        const marketCodes = await getUniqueMarketCodes();
        params.success([...marketCodes, null]);
      },
    },
    floatingFilter: true,
  },
  {
    headerName: 'Opentrack Status Container 1',
    field: 'opentrack_container_status',
    width: 125,
    cellRenderer: portIntegrationsFormatter,
    filter: 'agSetColumnFilter',
    filterParams: {
      buttons: ['reset', 'apply'],
      valueFormatter: (params) =>
        params.value
          ? titleize(humanize(i18next.t(sluggify(params.value))))
          : params.value,
      values: async (params) => {
        const openTrackStatuses = await getUniqueStatusList();
        params.success([...openTrackStatuses, null]);
      },
    },
    floatingFilter: true,
  },
  {
    headerName: 'Opentrack Status Container 2',
    field: 'opentrack_container_status_2',
    width: 125,
    cellRenderer: portIntegrationsFormatter,
    filter: 'agSetColumnFilter',
    filterParams: {
      buttons: ['reset', 'apply'],
      valueFormatter: (params) =>
        params.value
          ? titleize(humanize(i18next.t(sluggify(params.value))))
          : params.value,
      values: async (params) => {
        const openTrackStatuses = await getUniqueStatusList();
        params.success([...openTrackStatuses, null]);
      },
    },
    floatingFilter: true,
  },
  {
    headerName: 'Opentrack Tracking Status',
    field: 'opentrack_tracking_status',
    width: 125,
    cellRenderer: opentrackTrackingStatusFormatter,
    filter: 'agSetColumnFilter',
    filterParams: {
      buttons: ['reset', 'apply'],
      valueFormatter: (params) =>
        params.value
          ? titleize(humanize(i18next.t(sluggify(params.value))))
          : params.value,
      values: (params) => {
        const removedStatus = [
          OPENTRACK_TRACKING_STATUS.TRACKING_STOPPED,
          OPENTRACK_TRACKING_STATUS.TRACKING_INITIATED,
        ];
        const openTrackStatuses = Object.values(
          OPENTRACK_TRACKING_STATUS,
        ).filter((s) => !removedStatus.includes(s));
        params.success([...openTrackStatuses, null]);
      },
    },
    floatingFilter: true,
  },
  {
    headerName: 'Validated BOL',
    field: 'master_bol_status',
    width: 125,
    cellRenderer: masterBolStatusFormatter,
    filter: 'agSetColumnFilter',
    filterParams: {
      buttons: ['reset', 'apply'],
      valueFormatter: (params) =>
        params.value
          ? titleize(humanize(i18next.t(sluggify(params.value))))
          : params.value,
      values: (params) => {
        const masterBolStatuses = Object.values(MASTER_BOL_STATUS);
        params.success([...masterBolStatuses, null]);
      },
    },
    floatingFilter: true,
  },
  {
    headerName: 'Container Status',
    field: 'container_status',
    width: 125,
    cellRenderer: portIntegrationsFormatter,
    filter: 'agSetColumnFilter',
    filterParams: {
      buttons: ['reset', 'apply'],
      values: async (params) => {
        const statuses = await getContainerStatuses();
        params.success([...statuses, null]);
      },
    },
    floatingFilter: true,
  },
  {
    headerName: 'Location',
    titleize: true,
    field: 'address',
    width: 350,
    filterParams: {
      ...textDefaultFilterParams,
    },
  },
  {
    headerName: 'DO Status',
    field: 'delivery_order_status',
    cellRenderer: deliveryOrderFormatter,
    filterParams: {
      ...textDefaultFilterParams,
    },
  },
  {
    headerName: 'PU#',
    field: 'pickup_number',
    hide: true,
    filterParams: {
      ...textDefaultFilterParams,
    },
  },
  {
    headerName: 'SO#',
    field: 'so_number',
    hide: true,
    filterParams: {
      ...textDefaultFilterParams,
    },
  },
  {
    headerName: 'Shipper',
    titleize: true,
    field: 'shipper_name',
    cellRenderer: nameRenderer,
    filter: 'agMultiColumnFilter',
    filterParams: {
      filters: [
        {
          filter: CustomerMultiSearchFilter,
          filterParams: {
            // supressing FE filtering because of multifilter usage
            isUsedInMultifilter: true,
          },
        },
        {
          filter: TextFilterStartsWithNotStarts,
          filterParams: {
            // supressing FE filtering because of multifilter usage
            isUsedInMultifilter: true,
          },
        },
      ],
    },
  },
  {
    headerName: 'Shipper Ops Group',
    titleize: true,
    field: 'shipper_operations_group',
    filter: 'agSetColumnFilter',
    filterParams: {
      buttons: ['reset', 'apply'],
      defaultToNothingSelected: true,
      suppressMiniFilter: true,
      suppressSelectAll: true,
      values: [...OPS_GROUP_OPTIONS.map((option) => option.slug), null],
    },
  },
  {
    headerName: 'Shipper Ops Manager',
    titleize: true,
    field: 'shipper_operations_manager',
    filterParams: {
      ...textDefaultFilterParams,
    },
  },
  {
    headerName: 'BCO',
    field: 'bco',
    width: 100,
    filterParams: {
      ...textDefaultFilterParams,
    },
  },
  {
    headerName: 'Order Carrier',
    field: 'order_carrier_name',
    cellRenderer: nameRenderer,
    priority: 80,
    filterParams: {
      ...textDefaultFilterParams,
    },
  },
  {
    headerName: 'Shipment Carrier',
    field: 'carrier_name',
    cellRenderer: nameRenderer,
    priority: 80,
    filterParams: {
      ...textDefaultFilterParams,
      filterOptions: [
        'startsWith',
        {
          displayKey: 'notStartsWith',
          displayName: 'Does not start with',
          predicate: ([filterValue], cellValue) => {
            return !cellValue.startsWith(filterValue);
          },
        },
        'endsWith',
        'equals',
        'notEqual',
        'blank',
        'notBlank',
      ],
    },
  },
  {
    headerName: 'Driver',
    titleize: true,
    field: 'driver_name',
    priority: 55,
    filterParams: {
      ...textDefaultFilterParams,
    },
  },
  {
    headerName: 'Vessel',
    field: 'vessel_or_rail',
    filterParams: {
      ...textDefaultFilterParams,
    },
  },
  {
    headerName: 'Train',
    field: 'train',
    filterParams: {
      ...textDefaultFilterParams,
    },
    titleize: true,
  },
  {
    headerName: 'Terminal LFD',
    field: 'lfd',
    filter: 'agDateColumnFilter',
    valueFormatter: dateFormatter,
    schema_value: 'last_free_date',
    filterParams: dateServerSideFilterParams,
    cellDataType: 'dateString',
    editable: false,
    isDate: true,
    updateFunc: onDateCellEdit,
    floatingFilter: true,
  },
  {
    headerName: 'Yard LFD',
    field: 'yard_lfd',
    filter: 'agDateColumnFilter',
    valueFormatter: dateFormatter,
    schema_value: 'yard_last_free_date',
    filterParams: dateServerSideFilterParams,
    isDate: true,
    floatingFilter: true,
  },
  {
    headerName: 'Date Created',
    field: 'created_at',
    filter: 'agDateColumnFilter',
    valueFormatter: dateFormatter,
    isDate: true,
    filterParams: {
      ...dateServerSideFilterParams,
    },
    floatingFilter: true,
    sortingOrder: ['desc', 'asc'],
  },
  {
    headerName: 'Stop Arrived',
    field: 'arrived_date',
    filter: 'agDateColumnFilter',
    cellDataType: 'dateString',
    isDate: true,
    editable: false,
    filterParams: dateServerSideFilterParams,
    floatingFilter: true,
  },
  {
    headerName: 'ETA',
    field: 'eta',
    filter: 'agDateColumnFilter',
    valueFormatter: dateFormatter,
    schema_value: 'estimated_arrival',
    isDate: true,
    editable: false,
    filterParams: dateServerSideFilterParams,
    updateFunc: onDateCellEdit,
    floatingFilter: true,
  },
  {
    headerName: 'Missing Docs Report',
    field: 'missing_docs_report_sent',
    hide: true,
    cellRenderer: statusFormatter,
    filterParams: {
      ...textDefaultFilterParams,
    },
  },
  {
    headerName: 'Shipper Total',
    field: 'shipperCost',
    filter: 'agNumberColumnFilter',
    cellDataType: 'number',
    type: 'rightAligned',
    valueFormatter: (params) => {
      if (params && params.data && params.data.shipperCost >= 0) {
        return formatMoney(
          params.data.shipperCost,
          params.data?.shipment_currency,
        );
      }
      return '';
    },
    cellRenderer: (param) => {
      return param.valueFormatted || '';
    },
    filterParams: {
      ...numberDefaultFilterParams,
    },
  },
  {
    headerName: 'Carrier Total',
    field: 'carrierCost',
    filter: 'agNumberColumnFilter',
    cellDataType: 'number',
    type: 'rightAligned',
    valueFormatter: (params) => {
      if (params && params.data && params.data.carrierCost >= 0) {
        return formatMoney(
          params.data.carrierCost,
          params.data?.shipment_currency,
        );
      }
      return '';
    },
    cellRenderer: (param) => {
      return param.valueFormatted || '';
    },
    filterParams: {
      ...numberDefaultFilterParams,
    },
  },
  {
    headerName: 'Vendor Total',
    field: 'vendorCost',
    filter: 'agNumberColumnFilter',
    cellDataType: 'number',
    type: 'rightAligned',
    valueFormatter: (params) => {
      if (params && params.data && params.data.vendorCost >= 0) {
        return formatMoney(
          params.data.vendorCost,
          params.data?.shipment_currency,
        );
      }
      return '';
    },
    cellRenderer: (param) => {
      return param.valueFormatted || '';
    },
    filterParams: {
      ...numberDefaultFilterParams,
    },
  },
  {
    headerName: 'Base Rate',
    field: 'base_rate',
    filter: 'agNumberColumnFilter',
    cellDataType: 'number',
    type: 'rightAligned',
    filterParams: {
      ...numberDefaultFilterParams,
    },
  },
  {
    headerName: 'Shipper Chassis Total',
    titleize: true,
    field: 'chassis',
    filter: 'agNumberColumnFilter',
    cellDataType: 'number',
    type: 'rightAligned',
    filterParams: {
      ...numberDefaultFilterParams,
    },
  },
  {
    headerName: 'Shipper Detention Total',
    titleize: true,
    field: 'detention',
    filter: 'agNumberColumnFilter',
    cellDataType: 'number',
    type: 'rightAligned',
    filterParams: {
      ...numberDefaultFilterParams,
    },
  },
  {
    headerName: 'Shipper Storage Total',
    titleize: true,
    field: 'storage',
    filter: 'agNumberColumnFilter',
    cellDataType: 'number',
    type: 'rightAligned',
    filterParams: {
      ...numberDefaultFilterParams,
    },
  },
  {
    headerName: 'Other',
    titleize: true,
    field: 'other',
    filter: 'agNumberColumnFilter',
    cellDataType: 'number',
    type: 'rightAligned',
    filterParams: {
      ...numberDefaultFilterParams,
    },
  },
  {
    headerName: 'Total',
    titleize: true,
    field: 'total',
    filter: 'agNumberColumnFilter',
    cellDataType: 'number',
    type: 'rightAligned',
    filterParams: {
      ...numberDefaultFilterParams,
    },
  },
  {
    headerName: 'On Hold',
    field: 'on_hold_reason',
    cellRenderer: lineSeparatedStringFormatter,
    wrapText: true,
    autoHeight: true,
    filterParams: {
      ...textDefaultFilterParams,
    },
  },
  {
    headerName: 'POD Valid',
    field: 'is_pod_valid',
    cellRenderer: checkedFormatter,
    filter: 'agSetColumnFilter',
    filterParams: booleanDefaultFilterParams,
  },
  {
    headerName: 'Has Rail Events',
    field: 'has_rail_events',
    cellRenderer: checkedFormatter,
    filter: 'agSetColumnFilter',
    filterParams: booleanDefaultFilterParams,
  },
  {
    headerName: 'Days Since Delivery',
    field: 'days_since_loaded_delivery',
    filter: 'agNumberColumnFilter',
    cellDataType: 'number',
    type: 'rightAligned',
    filterParams: {
      ...numberDefaultFilterParams,
    },
  },
  {
    headerName: 'Days Since Delivery Bin',
    field: 'days_since_loaded_delivery_bin',
    filter: 'agSetColumnFilter',
    filterParams: {
      buttons: ['reset', 'apply'],
      values: [
        'Returned',
        '< Week',
        '< 2 Weeks',
        '< 1 Month',
        '< 2 Months',
        '< 3 Months',
        '3+ Months',
        null,
      ],
      // turn off standard sorting of values
      comparator: () => 1,
    },
  },
  {
    headerName: 'LFD Bin',
    field: 'last_free_date_bin',
    filter: 'agSetColumnFilter',
    filterParams: {
      buttons: ['reset', 'apply'],
      values: [
        'No LFD',
        'Week+ Before',
        '6+ Days Before LFD',
        '5 Days Before LFD',
        '4 Days Before LFD',
        '3 Days Before LFD',
        '2 Days Before LFD',
        '1 Day Before LFD',
        'LFD Today',
        '1 Day After LFD',
        '2 Days After LFD',
        '3 Days After LFD',
        '6+ Days After LFD',
      ],
      // turn off standard sorting of values
      comparator: () => 1,
    },
  },
  {
    headerName: 'Combo Outgated Date',
    field: 'combo_outgated_date',
    filter: 'agDateColumnFilter',
    valueFormatter: dateFormatter,
    filterParams: dateServerSideFilterParams,
  },
  {
    headerName: 'Combo LFD Date',
    field: 'combo_last_free_date',
    filter: 'agDateColumnFilter',
    valueFormatter: dateFormatter,
    filterParams: dateServerSideFilterParams,
  },
  {
    headerName: 'Outgated',
    field: 'outgated',
    cellRenderer: checkedFormatter,
    filter: 'agSetColumnFilter',
    filterParams: booleanDefaultFilterParams,
  },
  {
    headerName: 'Empty Returned',
    field: 'empty_returned',
    cellRenderer: checkedFormatter,
    filter: 'agSetColumnFilter',
    filterParams: booleanDefaultFilterParams,
  },
  {
    headerName: 'Loaded on Rail',
    field: 'rail_loaded',
    filter: 'agDateColumnFilter',
    valueFormatter: dateFormatter,
    isDate: true,
    filterParams: dateServerSideFilterParams,
    floatingFilter: true,
  },
  {
    headerName: 'Departed on Rail',
    field: 'rail_departed',
    filter: 'agDateColumnFilter',
    valueFormatter: dateFormatter,
    isDate: true,
    filterParams: dateServerSideFilterParams,
    floatingFilter: true,
  },
  {
    headerName: 'Arrived at Rail Facility',
    field: 'rail_arrived_at_facility',
    filter: 'agDateColumnFilter',
    valueFormatter: dateFormatter,
    isDate: true,
    filterParams: dateServerSideFilterParams,
    floatingFilter: true,
  },
  {
    headerName: 'Unloaded From Rail',
    field: 'rail_unloaded',
    filter: 'agDateColumnFilter',
    valueFormatter: dateFormatter,
    isDate: true,
    filterParams: dateServerSideFilterParams,
    floatingFilter: true,
  },
  {
    headerName: 'Available at Rail Facility',
    field: 'rail_available_at_facility',
    filter: 'agDateColumnFilter',
    valueFormatter: dateFormatter,
    isDate: true,
    filterParams: dateServerSideFilterParams,
    floatingFilter: true,
  },
  {
    headerName: 'Full Outgated From Rail Facility',
    field: 'rail_outgated_from_facility',
    filter: 'agDateColumnFilter',
    valueFormatter: dateFormatter,
    isDate: true,
    filterParams: dateServerSideFilterParams,
    floatingFilter: true,
  },
  {
    headerName: 'LFD at Rail Facility',
    field: 'rail_lfd_at_facility',
    filter: 'agDateColumnFilter',
    valueFormatter: dateFormatter,
    isDate: true,
    filterParams: dateServerSideFilterParams,
    floatingFilter: true,
  },
  {
    headerName: 'ETA to Final Rail Facility',
    field: 'rail_eta_final_facility',
    filter: 'agDateColumnFilter',
    valueFormatter: dateFormatter,
    isDate: true,
    filterParams: dateServerSideFilterParams,
    floatingFilter: true,
  },
  {
    headerName: 'Full Ingated at Rail Facility',
    field: 'rail_ingated_at_origin_facility',
    filter: 'agDateColumnFilter',
    valueFormatter: dateFormatter,
    isDate: true,
    filterParams: dateServerSideFilterParams,
    floatingFilter: true,
  },
  {
    headerName: 'Loaded on Rail at Origin',
    field: 'rail_loaded_at_origin',
    filter: 'agDateColumnFilter',
    valueFormatter: dateFormatter,
    isDate: true,
    filterParams: dateServerSideFilterParams,
    floatingFilter: true,
  },
  {
    headerName: 'Departed on Rail From Origin',
    field: 'rail_departed_from_origin',
    filter: 'agDateColumnFilter',
    valueFormatter: dateFormatter,
    isDate: true,
    filterParams: dateServerSideFilterParams,
    floatingFilter: true,
  },
  {
    headerName: 'Arrived At Origin Port Rail Facility',
    field: 'rail_arrived_at_origin_port_facility',
    filter: 'agDateColumnFilter',
    valueFormatter: dateFormatter,
    isDate: true,
    filterParams: dateServerSideFilterParams,
    floatingFilter: true,
  },
  {
    headerName: 'Unloaded From Rail at Origin Port',
    field: 'rail_unloaded_at_origin_port',
    filter: 'agDateColumnFilter',
    valueFormatter: dateFormatter,
    isDate: true,
    filterParams: dateServerSideFilterParams,
    floatingFilter: true,
  },
  {
    headerName: 'Empty Rail Billing Date',
    field: 'empty_rail_billing',
    filter: 'agDateColumnFilter',
    valueFormatter: dateFormatter,
    isDate: true,
    filterParams: dateServerSideFilterParams,
    floatingFilter: true,
  },
  {
    headerName: 'Loaded Rail Billing Date',
    field: 'loaded_rail_billing',
    filter: 'agDateColumnFilter',
    valueFormatter: dateFormatter,
    isDate: true,
    filterParams: dateServerSideFilterParams,
    floatingFilter: true,
  },
  {
    headerName: 'Must Arrive by Date',
    field: 'must_arrive_by_date',
    filter: 'agDateColumnFilter',
    valueFormatter: dateFormatter,
    isDate: true,
    filterParams: dateServerSideFilterParams,
    floatingFilter: true,
  },
  {
    headerName: 'Requested Pickup Date',
    field: 'requested_pickup_date',
    filter: 'agDateColumnFilter',
    valueFormatter: dateFormatter,
    isDate: true,
    filterParams: dateServerSideFilterParams,
    floatingFilter: true,
  },
  {
    headerName: 'Requested Delivery Appointment',
    field: 'requested_delivery_appointment',
    filter: 'agDateColumnFilter',
    valueFormatter: dateFormatter,
    isDate: true,
    filterParams: dateServerSideFilterParams,
    floatingFilter: true,
  },
  {
    headerName: 'Pending Shipper Accessorials',
    field: 'pending_shipper_accessorials',
    cellRenderer: checkedFormatter,
    filter: 'agSetColumnFilter',
    filterParams: booleanDefaultFilterParams,
  },
  {
    headerName: 'Pending Carrier Accessorials',
    field: 'pending_carrier_accessorials',
    cellRenderer: checkedFormatter,
    filter: 'agSetColumnFilter',
    filterParams: booleanDefaultFilterParams,
  },
  {
    headerName: 'Per Diem LFD',
    field: 'per_diem_lfd',
    cellRenderer: dateFormatter,
    filter: 'agDateColumnFilter',
    isDate: true,
    filterParams: dateServerSideFilterParams,
    floatingFilter: true,
  },
  {
    headerName: 'Automated Billing Evaluation Paused',
    field: 'shipment_automated_billing_status_evaluation_paused',
    cellRenderer: checkedFormatter,
    filter: 'agSetColumnFilter',
    filterParams: booleanDefaultFilterParams,
  },
  {
    headerName: 'Automated Billing Evaluation Paused Reason',
    field: 'shipment_automated_billing_status_evaluation_reason_code',
    filterParams: {
      ...textDefaultFilterParams,
    },
  },
  {
    headerName: 'Automated Billing Evaluated Paused Timestamp',
    field: 'shipment_automated_billing_status_evaluation_paused_timestamp',
    cellRenderer: dateFormatter,
    filter: 'agDateColumnFilter',
    isDate: true,
    filterParams: dateServerSideFilterParams,
    floatingFilter: true,
  },
  {
    headerName: 'Discussion',
    field: 'comments',
    autoHeight: true,
    editable: true,
    cellEditor: CommentsEditor,
    cellEditorParams: {
      onCommentAdd: onShipmentCommentAdd,
    },
    cellEditorPopup: true,
    filter: false,
    valueFormatter: (params) => {
      const count = params?.data?.comments?.length || 0;
      if (!count) {
        return '';
      }
      const postfix = count === 1 ? 'comment' : 'comments';
      return `${count} ${postfix}`;
    },
  },
  {
    headerName: 'Document Cutoff Date',
    field: 'document_cutoff_date',
    filter: 'agDateColumnFilter',
    valueFormatter: dateFormatter,
    filterParams: dateServerSideFilterParams,
  },
  {
    headerName: 'Rail Earliest Return',
    field: 'rail_earliest_return',
    filter: 'agDateColumnFilter',
    valueFormatter: dateFormatter,
    filterParams: dateServerSideFilterParams,
  },
  {
    headerName: 'Rail Cut Date',
    field: 'rail_cut_date',
    filter: 'agDateColumnFilter',
    valueFormatter: dateFormatter,
    filterParams: dateServerSideFilterParams,
  },
  {
    headerName: 'Total Invoiced',
    field: 'total_invoiced',
    filter: 'agNumberColumnFilter',
    cellDataType: 'number',
    headerTooltip: 'TOTAL_INVOICED_TOOLTIP',
    type: 'rightAligned',
    valueFormatter: (params) => {
      if (params?.data?.total_invoiced >= 0) {
        return formatMoney(
          params.data.total_invoiced,
          params.data?.shipment_currency,
        );
      }
      return '';
    },
  },
  {
    headerName: 'Unbilled Approved',
    field: 'unbilled_approved',
    filter: 'agNumberColumnFilter',
    cellDataType: 'number',
    headerTooltip: 'UNBILLED_APPROVED_TOOLTIP',
    type: 'rightAligned',
    valueFormatter: (params) => {
      if (params?.data?.unbilled_approved) {
        return formatMoney(
          params.data.unbilled_approved,
          params.data?.shipment_currency,
        );
      }
      return '';
    },
  },
  {
    headerName: 'Shipper Linehaul',
    field: 'shipper_linehaul',
    filter: 'agNumberColumnFilter',
    cellDataType: 'number',
    headerTooltip: 'SHIPPER_LINEHAUL_TOOLTIP',
    type: 'rightAligned',
    valueFormatter: (params) => {
      if (params?.data?.shipper_linehaul) {
        return formatMoney(
          params.data.shipper_linehaul,
          params.data?.shipment_currency,
        );
      }
      return '';
    },
  },
  {
    headerName: 'Team',
    groupId: 'team',
    hide: false,
    marryChildren: false,
    children: [
      {
        headerName: 'Commercial',
        groupId: 'commercial',
        hide: false,
        marryChildren: false,
        children: [
          {
            headerName: 'Sales Rep',
            field: 'commercial_sales_rep',
            filterParams: {
              ...textDefaultFilterParams,
            },
          },
          {
            headerName: 'Account Manager',
            field: 'commercial_account_manager',
            filterParams: {
              ...textDefaultFilterParams,
            },
          },
        ],
      },
      {
        headerName: 'Drayage Ops',
        groupId: 'drayage_ops',
        hide: false,
        marryChildren: false,
        children: [
          {
            headerName: 'Data Entry',
            field: 'drayage_ops_data_entry',
            filterParams: {
              ...textDefaultFilterParams,
            },
          },
          {
            headerName: 'Customer Success',
            field: 'drayage_ops_customer_success',
            filterParams: {
              ...textDefaultFilterParams,
            },
          },
        ],
      },
      {
        headerName: 'Shorthaul Ops',
        groupId: 'shorthaul_ops',
        hide: false,
        marryChildren: false,
        children: [
          {
            headerName: 'Operations Manager',
            field: 'shorthaul_ops_operations_manager',
            filterParams: {
              ...textDefaultFilterParams,
            },
          },
          {
            headerName: 'Customer Success',
            field: 'shorthaul_ops_customer_success',
            filterParams: {
              ...textDefaultFilterParams,
            },
          },
        ],
      },
    ],
  },
  {
    headerName: 'Exposed',
    field: 'exposed',
    cellRenderer: checkedFormatter,
    filter: 'agSetColumnFilter',
    filterParams: booleanDefaultFilterParams,
  },
];

const drayageColumns = COLUMN_DEFS.filter((c) => {
  return (
    [
      'order_carrier_name',
      'pickup_stop',
      'delivery_stop',
      'empty_delivery_stop',
      'pod_status',
      'mode',
      'move_type',
      'admin_notes',
      'notes',
      'cargo_description',
      'reference_numbers',
      'hazmat_description',
      'marketCode',
      'shipment_origin',
      'shipment_origin_name',
      'shipment_origin_city',
      'shipment_origin_state',
      'shipment_destination',
      'shipment_destination_name',
      'shipment_destination_city',
      'shipment_destination_state',
      'hazmat_class',
      'originating',
      'service_type',
      'hazmat_packaging_group',
      'steamship_or_railroad',
      'arrival_date',
      'discharge_date',
      'total_weight',
      'tags',
      'has_required_docs',
      'shipment_id',
      'shipmentStatus',
      'master_bol_status',
      'opentrack_container_status',
      'opentrack_container_status_2',
      'opentrack_tracking_status',
      'container_status',
      'order_id',
      'orderStatus',
      'equipment_id',
      'chassis_number',
      'trailer_number',
      'order_type',
      'shipperCost',
      'carrierCost',
      'vendorCost',
      'margin',
      'address',
      'delivery_order_status',
      'booking_number',
      'pickup_number',
      'so_number',
      'shipper_name',
      'bco',
      'carrier_name',
      'vessel_or_rail',
      'train',
      'lfd',
      'yard_lfd',
      'created_at',
      'review_date',
      'accounting_review_date',
      'eta',
      'missing_docs_report_sent',
      'base_rate',
      'chassis',
      'detention',
      'storage',
      'other',
      'total',
      'on_hold_reason',
      'is_pod_valid',
      'available_date',
      'outgated_date',
      'empty_returned_date',
      'empty_ready_for_pickup',
      'last_cut_date',
      'earliest_return_date',
      'earliest_empty_date',
      'arrived_date',
      'days_since_loaded_delivery',
      'days_since_loaded_delivery_bin',
      'last_free_date_bin',
      'has_rail_events',
      'combo_outgated_date',
      'combo_last_free_date',
      'outgated',
      'empty_returned',
      'rail_loaded',
      'rail_departed',
      'rail_arrived_at_facility',
      'rail_unloaded',
      'rail_available_at_facility',
      'rail_outgated_from_facility',
      'rail_lfd_at_facility',
      'rail_eta_final_facility',
      'rail_ingated_at_origin_facility',
      'rail_loaded_at_origin',
      'rail_departed_from_origin',
      'rail_arrived_at_origin_port_facility',
      'rail_unloaded_at_origin_port',
      'shipper_operations_group',
      'shipper_operations_manager',
      'empty_rail_billing',
      'loaded_rail_billing',
      'must_arrive_by_date',
      'requested_pickup_date',
      'requested_delivery_appointment',
      'pending_shipper_accessorials',
      'pending_carrier_accessorials',
      'per_diem_lfd',
      'billing_status',
      'comments',
      'document_cutoff_date',
      'rail_earliest_return',
      'rail_cut_date',
      'total_invoiced',
      'unbilled_pending',
      'unbilled_approved',
      'shipper_fsc_total',
      'shipper_linehaul',
      'team',
      'exposed',
      'shipment_automated_billing_status_evaluation_paused',
      'shipment_automated_billing_status_evaluation_reason_code',
      'shipment_automated_billing_status_evaluation_paused_timestamp',
      'billing_status_state_billing_status',
      'billing_status_state_pause_automated_billing_evaluation_reason_code',
      'billing_status_state_pause_automated_billing_evaluation_reason_codes',
      'billing_status_state_has_required_docs',
      'billing_status_state_missing_docs',
      'billing_status_state_has_required_reference_numbers',
      'billing_status_state_missing_reference_numbers',
      'billing_status_state_has_required_accessorials',
      'billing_status_state_missing_and_pending_required_accessorial_types',
      'billing_status_state_has_required_billable_stops',
      'billing_status_state_pending_billable_stops',
    ].indexOf(c.field || c.groupId) > -1
  );
});

const shorthaulColumns = COLUMN_DEFS.filter((c) => {
  return (
    [
      'shipment_id',
      'shipmentStatus',
      'closed',
      'canceled',
      'admin_notes',
      'notes',
      'shipper_name',
      'carrier_name',
      'tags',
      'marketCode',
      'driver_name',
      'driver_phone_number',
      'load_details',
      'reference_numbers',
      'days_since_loaded_delivery',
      'review_date',
      'shipment_origin',
      'shipment_origin_name',
      'shipment_origin_city',
      'shipment_origin_state',
      'shipment_destination',
      'shipment_destination_name',
      'shipment_destination_city',
      'shipment_destination_state',
      'shipment_type',
      'carrierCost',
      'shipperCost',
      'pod_status',
      'is_pod_valid',
      'created_at',
      'arrived_date',
      'shipper_operations_group',
      'shipper_operations_manager',
      'must_arrive_by_date',
      'requested_pickup_date',
      'requested_delivery_appointment',
      'per_diem_lfd',
      'billing_status',
      'comments',
      'document_cutoff_date',
      'rail_earliest_return',
      'rail_cut_date',
      'team',
    ].indexOf(c.field || c.groupId) > -1
  );
});

const getAdminShorthaulColumns = () => {
  return sortBy(
    shorthaulColumns
      .concat(...getReferenceNumberGroup())
      .concat(...getShorthaulStopGroupsColumnDefs())
      .map((c) => {
        if (c.pinnedFor && c.pinnedFor.indexOf('shorthaul') > -1) {
          c.pinned = true;
        }
        return c;
      }),
    'priority',
  );
};

const getAdminDrayageColumns = () => {
  const columns = getBillingStatusStateGroup()
    .concat(...getReferenceNumberGroup())
    .concat(...drayageColumns)
    .concat(...getDrayageStopGroupsColumnDefs(), {
      ...getDrayageShipmentTypes(),
    })
    .map((c) => {
      const col = { ...c };
      if (DEFAULT_DRAYAGE_COLS_OPS.includes(col.field)) {
        col.hide = false;
      } else {
        col.hide = true;
      }

      if (col.pinnedFor && col.pinnedFor.indexOf('drayage') > -1) {
        col.pinned = true;
      }
      return col;
    });

  return sortBy(columns, 'priority');
};

export const getDrayageColumns = () => {
  return getAdminDrayageColumns();
};

export const getShorthaulColumns = () => {
  return getAdminShorthaulColumns();
};

export const allColumns = COLUMN_DEFS;

export const allFilterOptions = [
  ...textDefaultFilterParams.filterOptions,
  ...dateServerSideFilterParams.filterOptions,
];

export const getColumnDefs = (view, t) => {
  const columns =
    view === 'drayage' ? getDrayageColumns() : getShorthaulColumns();
  return columns
    .map((incoming) => {
      const column = { ...incoming };
      column.menuTabs = ['filterMenuTab', 'generalMenuTab'];
      if (column.field === 'created_at') {
        column.filterParams.minValidDate = getMinValidDate();
      }
      if (
        isUS() &&
        column.field &&
        column.field.toLowerCase().includes('state')
      ) {
        column.filter = 'agSetColumnFilter';
        column.floatingFilter = true;
        column.filterParams = {
          values: usStates.map((state) => state.abbreviation),
        };
      }

      return column;
    })
    .map((c) => {
      if (c.field === 'margin') {
        c.cellRenderer = 'marginFormatter';
      }
      c.headerName = c.titleize
        ? firstLetterTitlization(t(sluggify(c.headerName)))
        : t(sluggify(c.headerName));
      return c;
    });
};

export const getShipperBlacklist = async () => {
  const blacklistedColumns = await getColumnBlacklist();
  return { blacklistedColumns };
};

export const getShipperDrayageColumns = () => {
  const adminColumns = getAdminDrayageColumns();
  return adminColumns.map((col) => {
    if (col.field === 'shipment_id') {
      col.cellRenderer = shipperShipmentIDRenderer;
    }
    return col;
  });
};

export const getShipperShorthaulColumns = async (t) => {
  const columnDefs = getColumnDefs('shorthaul', t);
  const blacklistedColumns = await getColumnBlacklist();
  const filteredColumns = columnDefs.filter(
    (col) => !blacklistedColumns[col.field],
  );
  return { filteredColumns, blacklistedColumns };
};

export const filterOptionSlugs = allFilterOptions.reduce(
  (acc, filterOption) => {
    if (typeof filterOption === 'string') {
      acc[filterOption] = sluggify(filterOption);
    } else {
      acc[filterOption.displayKey] = sluggify(filterOption.displayName);
    }

    return acc;
  },
  {},
);
